import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useScene, useScenes } from "@/lib/scenes";
import { useTheme } from "@/lib/theme";
import {
  pointsToString,
  insertPointAtNearestEdge,
  polygonsOverlap,
  type Point,
} from "@/lib/polygon";
import {
  loadSamModel,
  encodeImage,
  segmentClick,
  isModelLoaded,
  type ImageEmbedding,
} from "@/lib/sam-client";

type BuilderItem = {
  id: string;
  english: string;
  polygons: Point[][];
};

type QueuedClick = {
  id: number;
  itemId: string;
  x: number;
  y: number;
  status: "queued" | "processing" | "done" | "failed";
};

type EditorMode = "sam" | "edit";

import { DiffOverlay, type DiffPayload } from "@/components/admin/DiffOverlay";

type DragState =
  | { mode: "handle"; polyIdx: number; pointIdx: number }
  | { mode: "polygon"; polyIdx: number; start: Point; original: Point[] }
  | null;

const PALETTE = [
  "#e6194b", "#3cb44b", "#4363d8", "#f58231", "#911eb4",
  "#42d4f4", "#f032e6", "#bfef45", "#fabed4", "#469990",
  "#dcbeff", "#9a6324", "#800000", "#aaffc3", "#808000",
  "#000075", "#a9a9a9",
];

type SamStatus = "idle" | "downloading" | "encoding" | "ready" | "failed";

export default function SceneBuilderPage() {
  const { sceneSlug } = useParams<{ sceneSlug: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  // The accent color is light-pink in light mode; default white text is
  // unreadable on it. Flip text to black for any accent-backed element.
  const onAccent = theme === "light" ? "#000" : "#fff";
  const scene = useScene(sceneSlug);
  const allScenes = useScenes();
  const sceneOptions = useMemo(
    () => (allScenes ?? [])
      .map((s) => ({ slug: s.slug, label: s.label }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [allScenes]
  );
  const [draftSceneSet, setDraftSceneSet] = useState<Set<string>>(new Set());

  const [items, setItems] = useState<BuilderItem[]>([]);
  const [newLabel, setNewLabel] = useState("");
  const [filter, setFilter] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<EditorMode>("sam");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  // SAM
  const [samStatus, setSamStatus] = useState<SamStatus>(isModelLoaded() ? "encoding" : "idle");
  const [samProgress, setSamProgress] = useState("");
  const embeddingRef = useRef<ImageEmbedding | null>(null);

  // Click queue
  const [queue, setQueue] = useState<QueuedClick[]>([]);
  const processingRef = useRef(false);
  const nextClickId = useRef(0);

  // Edit mode
  const dragRef = useRef<DragState>(null);
  const overlayRef = useRef<SVGSVGElement>(null);
  const [activePolyIdx, setActivePolyIdx] = useState(0);

  // Import file picker
  const importInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  // Keyboard cheatsheet overlay
  const [showHelp, setShowHelp] = useState(false);

  // Pre-publish diff modal
  const [diffPayload, setDiffPayload] = useState<DiffPayload | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);

  // Refs for keyboard-shortcut focus targets
  const filterInputRef = useRef<HTMLInputElement>(null);
  const addInputRef = useRef<HTMLInputElement>(null);

  // Drag-to-reorder state
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  // Hovered item (for canvas preview)
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Auto-save (opt-in, persisted)
  const AUTOSAVE_KEY = "tapvocab.sceneBuilder.autoSave";
  const [autoSave, setAutoSave] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(AUTOSAVE_KEY) === "1";
  });
  useEffect(() => {
    try { localStorage.setItem(AUTOSAVE_KEY, autoSave ? "1" : "0"); } catch { /* ignore */ }
  }, [autoSave]);
  const reorderItem = useCallback((fromId: string, toId: string) => {
    if (fromId === toId) return;
    setItems((prev) => {
      const fromIdx = prev.findIndex((it) => it.id === fromId);
      const toIdx = prev.findIndex((it) => it.id === toId);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  }, []);

  // Forward-ref to saveToServer so the keyboard handler can call it without
  // depending on a callback declared further down the file.
  const saveToServerRef = useRef<() => void>(() => {});
  // Tracks the pending "clear save message" timer so rapid saves don't stack
  // setState calls and the timer is cancelled if the component unmounts.
  const saveMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Forward-ref pattern for the active-polygon delete (Backspace/Delete)
  // and the current editor mode, both consumed by the global keyboard handler.
  const deleteActivePolygonRef = useRef<() => void>(() => {});
  const cyclePolygonRef = useRef<(dir: 1 | -1) => void>(() => {});
  const editorModeRef = useRef<EditorMode>("sam");

  // Resizable sidebar (persisted)
  const SIDEBAR_KEY = "tapvocab.sceneBuilder.sidebarWidth";
  const SIDEBAR_MIN = 220;
  const SIDEBAR_MAX = 560;
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    if (typeof window === "undefined") return 320;
    const v = Number(localStorage.getItem(SIDEBAR_KEY));
    return v >= SIDEBAR_MIN && v <= SIDEBAR_MAX ? v : 320;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const onResizeStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    resizeRef.current = { startX: e.clientX, startWidth: sidebarWidth };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  }, [sidebarWidth]);
  const onResizeMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeRef.current) return;
    const dx = e.clientX - resizeRef.current.startX;
    const next = Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, resizeRef.current.startWidth + dx));
    setSidebarWidth(next);
  }, []);
  const onResizeEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeRef.current) return;
    resizeRef.current = null;
    try { localStorage.setItem(SIDEBAR_KEY, String(sidebarWidth)); } catch { /* quota / private mode */ }
    (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
  }, [sidebarWidth]);

  // "Dirty" = there are unsaved local edits since the last load/save.
  // Used to warn before navigating away with beforeunload.
  const [dirty, setDirty] = useState(false);
  const savedSnapshotRef = useRef<string>("");

  useEffect(() => {
    setDirty(JSON.stringify(items) !== savedSnapshotRef.current);
  }, [items]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required for the browser to actually show its native prompt
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Auto-save: debounce 2s after the last dirtying edit, then save.
  useEffect(() => {
    if (!autoSave || !dirty) return;
    const t = setTimeout(() => { saveToServerRef.current(); }, 2000);
    return () => clearTimeout(t);
  }, [autoSave, dirty, items]);

  // Undo / Redo
  const [history, setHistory] = useState<BuilderItem[][]>([]);
  const [future, setFuture] = useState<BuilderItem[][]>([]);
  const snapshot = useCallback(
    (src: BuilderItem[]) => src.map((it) => ({ ...it, polygons: it.polygons.map((p) => [...p]) })),
    []
  );
  const pushHistory = useCallback(() => {
    setHistory((prev) => {
      const next = [...prev, snapshot(items)];
      if (next.length > 100) next.shift();
      return next;
    });
    setFuture([]);
  }, [items, snapshot]);
  const undo = useCallback(() => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setFuture((f) => [...f, snapshot(items)]);
    setItems(last);
  }, [history, items, snapshot]);
  const redo = useCallback(() => {
    if (future.length === 0) return;
    const next = future[future.length - 1];
    setFuture((f) => f.slice(0, -1));
    setHistory((h) => [...h, snapshot(items)]);
    setItems(next);
  }, [future, items, snapshot]);

  const sceneWidth = scene?.width ?? 1536;
  const sceneHeight = scene?.height ?? 1024;
  const selectedItem = items.find((it) => it.id === selectedId) ?? null;
  const mappedCount = items.filter((it) => it.polygons.length > 0).length;
  const unmappedCount = items.length - mappedCount;
  const pendingClicks = queue.filter((q) => q.status === "queued" || q.status === "processing").length;

  // Filtered view of items by the search box (substring match on english, case-insensitive)
  const filteredItems = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.english.toLowerCase().includes(q) || it.id.toLowerCase().includes(q));
  }, [items, filter]);

  // Overlap detection: items overlap if any polygon of A intersects any polygon of B.
  // Quick visual hint that two hotspots are stepping on each other.
  const overlappingIds = useMemo(() => {
    const ids = new Set<string>();
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i];
        const b = items[j];
        let hit = false;
        outer: for (const pa of a.polygons) {
          for (const pb of b.polygons) {
            if (polygonsOverlap(pa, pb)) { hit = true; break outer; }
          }
        }
        if (hit) { ids.add(a.id); ids.add(b.id); }
      }
    }
    return ids;
  }, [items]);

  // Load items from DB (draft tables, falling back to live as the starting state)
  const loadFromServer = useCallback(() => {
    if (!sceneSlug || !scene) return;
    let cancelled = false;
    const applyLoaded = (loaded: BuilderItem[]) => {
      setItems(loaded);
      savedSnapshotRef.current = JSON.stringify(loaded);
    };
    fetch(`/api/admin/scenes/${sceneSlug}/draft`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        setHasDraft(Boolean(data?.hasDraft));
        if (data?.items?.length > 0) {
          applyLoaded(
            (data.items as { id: string; english: string; shape: { points: unknown } }[]).map((it) => {
              const pts = it.shape?.points;
              const arr = Array.isArray(pts) ? pts as Point[][] | Point[] : [];
              const polygons = arr.length > 0 && Array.isArray(arr[0]) && typeof arr[0][0] === "number"
                ? [arr as Point[]]
                : arr as Point[][];
              return { id: it.id, english: it.english, polygons };
            })
          );
        } else {
          applyLoaded([]);
        }
      })
      .catch(() => {
        if (!cancelled) applyLoaded([]);
      });
    return () => { cancelled = true; };
  }, [sceneSlug, scene]);

  useEffect(() => loadFromServer(), [loadFromServer]);

  // Track which scenes have unpublished drafts (for the scene switcher)
  useEffect(() => {
    let cancelled = false;
    fetch("/api/admin/scene-data/drafts", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { scenes: [] }))
      .then((data: { scenes?: string[] }) => {
        if (!cancelled) setDraftSceneSet(new Set(data.scenes ?? []));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [hasDraft]); // refetch when this scene's draft state flips so the dropdown stays accurate

  // Load browser SAM
  useEffect(() => {
    if (!scene) return;
    let cancelled = false;
    async function init() {
      try {
        if (!isModelLoaded()) {
          setSamStatus("downloading");
          setSamProgress("Downloading SAM model...");
          await loadSamModel((p) => {
            if (cancelled) return;
            if (p.file && p.progress != null)
              setSamProgress(`${p.file.split("/").pop()}: ${Math.round(p.progress)}%`);
          });
        }
        if (cancelled) return;
        setSamStatus("encoding");
        setSamProgress("Encoding image...");
        embeddingRef.current = await encodeImage(scene!.image);
        if (cancelled) return;
        setSamStatus("ready");
        setSamProgress("");
      } catch (err) {
        if (!cancelled) {
          console.error("Browser SAM failed:", err);
          setSamStatus("failed");
          setSamProgress("");
        }
      }
    }
    init();
    return () => { cancelled = true; };
  }, [scene]);

  // Process SAM queue
  useEffect(() => {
    if (processingRef.current) return;
    const next = queue.find((q) => q.status === "queued");
    if (!next || samStatus !== "ready") return;
    processingRef.current = true;
    setQueue((prev) => prev.map((q) =>
      q.id === next.id ? { ...q, status: "processing" as const } : q
    ));
    segmentClick(embeddingRef.current!, next.x, next.y)
      .then((result) => {
        if (result.polygon && result.polygon.length >= 3) {
          setItems((prev) => prev.map((it) =>
            it.id === next.itemId
              ? { ...it, polygons: [...it.polygons, result.polygon] }
              : it
          ));
        }
        setQueue((prev) => prev.map((q) =>
          q.id === next.id ? { ...q, status: "done" as const } : q
        ));
      })
      .catch(() => {
        setQueue((prev) => prev.map((q) =>
          q.id === next.id ? { ...q, status: "failed" as const } : q
        ));
      })
      .finally(() => { processingRef.current = false; });
  }, [queue, samStatus]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setShowHelp(false); setDiffPayload(null); return; }
      // Ctrl/Cmd+S works even from inputs (standard expectation)
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        saveToServerRef.current();
        return;
      }
      const target = e.target;
      if (target instanceof HTMLElement && target.closest("input, textarea, select")) return;
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault(); redo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault(); undo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === "y") {
        e.preventDefault(); redo();
      }
      if (e.key === "1") setEditorMode("sam");
      if (e.key === "2") setEditorMode("edit");
      if (e.key === "/") {
        e.preventDefault();
        filterInputRef.current?.focus();
      }
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        addInputRef.current?.focus();
      }
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setShowHelp((s) => !s);
      }
      // Delete the active polygon when in Edit mode
      if ((e.key === "Backspace" || e.key === "Delete") && editorModeRef.current === "edit") {
        e.preventDefault();
        deleteActivePolygonRef.current();
      }
      // Tab cycles through the active item's polygons in Edit mode
      if (e.key === "Tab" && editorModeRef.current === "edit") {
        e.preventDefault();
        cyclePolygonRef.current(e.shiftKey ? -1 : 1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo]);

  // Cancel any pending save-message timer when the component unmounts so the
  // 5s setSaveMsg(null) doesn't fire on a torn-down tree.
  useEffect(() => {
    return () => {
      if (saveMsgTimerRef.current) {
        clearTimeout(saveMsgTimerRef.current);
        saveMsgTimerRef.current = null;
      }
    };
  }, []);

  // ---- Item management ----

  const addItem = useCallback(() => {
    const label = newLabel.trim().toLowerCase();
    if (!label) return;
    const existing = items.find((it) => it.english === label);
    if (existing) { setSelectedId(existing.id); setNewLabel(""); return; }
    setItems((prev) => [...prev, { id: label, english: label, polygons: [] }]);
    setSelectedId(label);
    setNewLabel("");
  }, [newLabel, items]);

  const removeItem = useCallback((id: string) => {
    pushHistory();
    setItems((prev) => prev.filter((it) => it.id !== id));
    // Drop any queued / processing SAM clicks for this item — otherwise the
    // pendingClicks counter would block Save indefinitely on a ghost item.
    // (An in-flight SAM call can't actually be cancelled, but its result
    //  setItems(...) is a no-op once the item is gone.)
    setQueue((prev) => prev.filter((q) => q.itemId !== id));
    if (selectedId === id) setSelectedId(null);
  }, [selectedId, pushHistory]);

  const startRename = useCallback((item: BuilderItem) => {
    setEditingId(item.id);
    setEditingLabel(item.english);
  }, []);

  const commitRename = useCallback(() => {
    if (!editingId || !editingLabel.trim()) { setEditingId(null); return; }
    setItems((prev) => prev.map((it) =>
      it.id === editingId ? { ...it, english: editingLabel.trim().toLowerCase() } : it
    ));
    setEditingId(null);
  }, [editingId, editingLabel]);

  // ---- SVG coordinate helper ----

  const clientToSvg = useCallback((e: ReactPointerEvent | PointerEvent): Point => {
    const el = overlayRef.current;
    if (!el) return [0, 0];
    const bounds = el.getBoundingClientRect();
    return [
      Math.round(((e.clientX - bounds.left) / bounds.width) * sceneWidth),
      Math.round(((e.clientY - bounds.top) / bounds.height) * sceneHeight),
    ];
  }, [sceneWidth, sceneHeight]);

  // ---- SAM mode: click → queue ----

  const handleSamClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!scene || !selectedId || samStatus !== "ready") {
      if (samStatus !== "ready") setError("SAM is still loading...");
      return;
    }
    setError(null);
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * sceneWidth;
    const y = ((e.clientY - rect.top) / rect.height) * sceneHeight;
    setQueue((prev) => [
      ...prev,
      { id: nextClickId.current++, itemId: selectedId, x, y, status: "queued" },
    ]);
  }, [scene, selectedId, samStatus, sceneWidth, sceneHeight]);

  // ---- Edit mode: drag polygon points ----

  const handleEditPointerDown = useCallback((e: ReactPointerEvent<SVGSVGElement>) => {
    if (!selectedItem || selectedItem.polygons.length === 0) return;
    // Don't start drag if clicking on a circle handle — that's handled separately
    if ((e.target as SVGElement).tagName === "circle") return;
    // Check if click is inside the active polygon → move whole polygon
    const poly = selectedItem.polygons[activePolyIdx];
    if (!poly) return;
    pushHistory();
    const pt = clientToSvg(e);
    dragRef.current = { mode: "polygon", polyIdx: activePolyIdx, start: pt, original: poly.map((p) => [...p] as Point) };
    (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId);
  }, [selectedItem, activePolyIdx, pushHistory, clientToSvg]);

  const handleHandlePointerDown = useCallback((e: ReactPointerEvent<SVGCircleElement>, polyIdx: number, pointIdx: number) => {
    e.stopPropagation();
    pushHistory();
    dragRef.current = { mode: "handle", polyIdx, pointIdx };
    (e.currentTarget as SVGCircleElement).setPointerCapture(e.pointerId);
  }, [pushHistory]);

  const handlePointerMove = useCallback((e: ReactPointerEvent<SVGSVGElement>) => {
    if (!dragRef.current || !selectedId) return;
    const drag = dragRef.current;
    const pt = clientToSvg(e);
    setItems((prev) => prev.map((it) => {
      if (it.id !== selectedId) return it;
      const polys = it.polygons.map((p, pi) => {
        if (pi !== drag.polyIdx) return p;
        if (drag.mode === "handle") {
          const pts = [...p];
          pts[drag.pointIdx] = pt;
          return pts;
        } else {
          const dx = pt[0] - drag.start[0];
          const dy = pt[1] - drag.start[1];
          return drag.original.map(([x, y]) => [x + dx, y + dy] as Point);
        }
      });
      return { ...it, polygons: polys };
    }));
  }, [selectedId, clientToSvg]);

  const handlePointerUp = useCallback(() => { dragRef.current = null; }, []);

  const handleEditClick = useCallback((e: ReactPointerEvent<SVGSVGElement>) => {
    if (!selectedItem || !e.shiftKey) return;
    const poly = selectedItem.polygons[activePolyIdx];
    if (!poly) return;
    pushHistory();
    const pt = clientToSvg(e);
    const newPoly = insertPointAtNearestEdge(poly, pt);
    setItems((prev) => prev.map((it) =>
      it.id === selectedId
        ? { ...it, polygons: it.polygons.map((p, pi) => pi === activePolyIdx ? newPoly : p) }
        : it
    ));
  }, [selectedItem, activePolyIdx, pushHistory, clientToSvg, selectedId]);

  const handleHandleClick = useCallback((e: ReactPointerEvent<SVGCircleElement>, polyIdx: number, pointIdx: number) => {
    if (!e.altKey || !selectedItem) return;
    const poly = selectedItem.polygons[polyIdx];
    if (!poly || poly.length <= 3) return;
    pushHistory();
    setItems((prev) => prev.map((it) =>
      it.id === selectedId
        ? { ...it, polygons: it.polygons.map((p, pi) => pi === polyIdx ? p.filter((_, i) => i !== pointIdx) : p) }
        : it
    ));
  }, [selectedItem, pushHistory, selectedId]);

  // ---- Save ----

  const saveToServer = useCallback(async () => {
    if (!sceneSlug) return;
    const mapped = items.filter((it) => it.polygons.length > 0);
    if (mapped.length === 0) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch("/api/admin/scene-items", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          sceneId: sceneSlug,
          items: mapped.map((it) => ({ id: it.id, english: it.english, polygon: it.polygons })),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "save failed" }));
        setSaveMsg(`Error: ${(err as { error: string }).error}`);
      } else {
        const data = (await res.json()) as { translatedCount?: number };
        setHasDraft(true);
        savedSnapshotRef.current = JSON.stringify(items);
        setSaveMsg(`Saved to draft${data.translatedCount ? ` (+${data.translatedCount} translations)` : ""}`);
        if (saveMsgTimerRef.current) clearTimeout(saveMsgTimerRef.current);
        saveMsgTimerRef.current = setTimeout(() => {
          setSaveMsg(null);
          saveMsgTimerRef.current = null;
        }, 5000);
      }
    } catch { setSaveMsg("Network error"); }
    finally { setSaving(false); }
  }, [sceneSlug, items]);

  // Keep the keyboard handler's stable reference pointing at the latest saveToServer
  useEffect(() => {
    saveToServerRef.current = saveToServer;
  }, [saveToServer]);

  // Active-polygon delete: shared between the keyboard handler and the × button
  const deleteActivePolygon = useCallback(() => {
    if (!selectedItem || selectedItem.polygons.length === 0) return;
    pushHistory();
    const itemId = selectedItem.id;
    const pi = activePolyIdx;
    setItems((prev) => prev.map((it) =>
      it.id === itemId
        ? { ...it, polygons: it.polygons.filter((_, i) => i !== pi) }
        : it
    ));
    setActivePolyIdx((prev) => Math.max(0, prev - 1));
  }, [selectedItem, activePolyIdx, pushHistory]);

  useEffect(() => {
    deleteActivePolygonRef.current = deleteActivePolygon;
  }, [deleteActivePolygon]);

  const cyclePolygon = useCallback((dir: 1 | -1) => {
    const n = selectedItem?.polygons.length ?? 0;
    if (n <= 1) return;
    setActivePolyIdx((cur) => (cur + dir + n) % n);
  }, [selectedItem]);

  useEffect(() => {
    cyclePolygonRef.current = cyclePolygon;
  }, [cyclePolygon]);

  useEffect(() => {
    editorModeRef.current = editorMode;
  }, [editorMode]);

  const openPublishDiff = useCallback(async () => {
    if (!sceneSlug || !hasDraft) return;
    setDiffLoading(true);
    setSaveMsg(null);
    try {
      const res = await fetch(`/api/admin/scenes/${sceneSlug}/diff`, { credentials: "same-origin" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "diff failed" }));
        setSaveMsg(`Error: ${(err as { error: string }).error}`);
        return;
      }
      const data = (await res.json()) as DiffPayload;
      setDiffPayload(data);
    } catch { setSaveMsg("Network error"); }
    finally { setDiffLoading(false); }
  }, [sceneSlug, hasDraft]);

  const confirmPublish = useCallback(async () => {
    if (!sceneSlug) return;
    setDiffPayload(null);
    setPublishing(true);
    setSaveMsg(null);
    try {
      const res = await fetch("/api/admin/scene-data/publish", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ sceneId: sceneSlug }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "publish failed" }));
        setSaveMsg(`Error: ${(err as { error: string }).error}`);
      } else {
        const data = (await res.json()) as { itemCount: number };
        setHasDraft(false);
        setSaveMsg(`Published ${data.itemCount} items live`);
        setTimeout(() => setSaveMsg(null), 5000);
        loadFromServer();
      }
    } catch { setSaveMsg("Network error"); }
    finally { setPublishing(false); }
  }, [sceneSlug, loadFromServer]);

  const exportScene = useCallback(() => {
    if (!sceneSlug) return;
    // Trigger a browser download via a hidden link to the export endpoint.
    // The endpoint sets Content-Disposition so the filename is server-chosen.
    window.location.href = `/api/admin/scenes/${sceneSlug}/export`;
  }, [sceneSlug]);

  const importScene = useCallback(
    async (file: File) => {
      if (!sceneSlug) return;
      setImporting(true);
      setSaveMsg(null);
      try {
        const text = await file.text();
        let payload: unknown;
        try {
          payload = JSON.parse(text);
        } catch {
          setSaveMsg("Error: file is not valid JSON");
          return;
        }
        const res = await fetch(`/api/admin/scenes/${sceneSlug}/import`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: "import failed" }));
          setSaveMsg(`Error: ${(err as { error: string }).error}`);
        } else {
          const data = (await res.json()) as { itemCount: number; translationCount: number };
          setHasDraft(true);
          setSaveMsg(`Imported to draft: ${data.itemCount} items, ${data.translationCount} translations`);
          setTimeout(() => setSaveMsg(null), 6000);
          loadFromServer();
        }
      } catch {
        setSaveMsg("Network error");
      } finally {
        setImporting(false);
      }
    },
    [sceneSlug, loadFromServer]
  );

  const discardDraft = useCallback(async () => {
    if (!sceneSlug || !hasDraft) return;
    if (!confirm("Discard draft? Your unpublished changes will be lost.")) return;
    setDiscarding(true);
    setSaveMsg(null);
    try {
      const res = await fetch("/api/admin/scene-data/discard", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ sceneId: sceneSlug }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "discard failed" }));
        setSaveMsg(`Error: ${(err as { error: string }).error}`);
      } else {
        setHasDraft(false);
        setSaveMsg("Draft discarded");
        setTimeout(() => setSaveMsg(null), 5000);
        loadFromServer();
      }
    } catch { setSaveMsg("Network error"); }
    finally { setDiscarding(false); }
  }, [sceneSlug, hasDraft, loadFromServer]);

  if (!scene) {
    return <p style={{ padding: 40 }}>Scene &ldquo;{sceneSlug}&rdquo; not found.</p>;
  }

  const samLoading = samStatus === "downloading" || samStatus === "encoding";
  const canClick = samStatus === "ready" && selectedId !== null;
  const selectedIdx = selectedItem ? items.indexOf(selectedItem) : -1;
  const selectedColor = selectedIdx >= 0 ? PALETTE[selectedIdx % PALETTE.length] : "#888";

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "100vh", overflow: "hidden",
    }}>
      <div style={{
        flexShrink: 0, zIndex: 10,
        display: "flex", alignItems: "center", gap: 12,
        padding: "8px 16px",
        background: "var(--bg, #fff)",
        borderBottom: "1px solid var(--border)",
        flexWrap: "wrap",
      }}>
        <Link to="/admin" className="ghost-link" style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
          ← Dashboard
        </Link>
        <select
          value={sceneSlug ?? ""}
          onChange={(e) => navigate(`/admin/scene-builder/${e.target.value}`)}
          aria-label="Switch scene"
          style={{
            fontWeight: 700, fontSize: "0.95rem",
            padding: "4px 8px", borderRadius: 6,
            border: "1px solid var(--border)",
            background: "var(--surface)", color: "var(--text)",
            cursor: "pointer", maxWidth: 240,
          }}
        >
          {/* Fallback option for when sceneSlug doesn't match a known scene
              (otherwise browsers silently show the first option, which made
              the dropdown look like it was on Airport while editing Kitchen). */}
          {sceneSlug && !sceneOptions.some((s) => s.slug === sceneSlug) && (
            <option value={sceneSlug}>{sceneSlug} (unknown)</option>
          )}
          {sceneOptions.map((s) => (
            <option key={s.slug} value={s.slug}>
              {draftSceneSet.has(s.slug) ? "● " : ""}{s.label}
            </option>
          ))}
        </select>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "3px 10px", borderRadius: 999,
          background: hasDraft ? "rgba(245, 130, 49, 0.12)" : "rgba(60, 180, 75, 0.12)",
          border: `1px solid ${hasDraft ? "#f58231" : "#3cb44b"}`,
          fontSize: "0.78rem", fontWeight: 600, whiteSpace: "nowrap",
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: 3,
            background: hasDraft ? "#f58231" : "#3cb44b",
          }} />
          {hasDraft ? "Draft" : "Live"}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="primary-button" onClick={saveToServer}
          disabled={mappedCount === 0 || saving || pendingClicks > 0 || samLoading}
          style={{ padding: "6px 14px", fontSize: "0.85rem" }}>
          {saving ? "Saving..." : pendingClicks > 0 ? `Wait (${pendingClicks}...)` : `Save draft (${mappedCount})`}
        </button>
        <button type="button" className="primary-button" onClick={openPublishDiff}
          disabled={!hasDraft || publishing || diffLoading || saving || pendingClicks > 0 || samLoading}
          style={{
            padding: "6px 14px", fontSize: "0.85rem",
            background: hasDraft ? "var(--accent)" : undefined,
            color: hasDraft ? onAccent : undefined,
          }}>
          {publishing ? "Publishing..." : diffLoading ? "Diffing..." : "Publish"}
        </button>
        <OverflowMenu disabled={samLoading}>
          <MenuItem onClick={discardDraft} disabled={!hasDraft || discarding}>
            {discarding ? "Discarding..." : "Discard draft"}
          </MenuItem>
          <MenuDivider />
          <MenuItem onClick={exportScene} title="Download live scene as JSON">
            ↓ Export scene
          </MenuItem>
          <MenuItem
            onClick={() => importInputRef.current?.click()}
            disabled={importing}
            title="Replace draft with a JSON file"
          >
            {importing ? "Importing..." : "↑ Import scene"}
          </MenuItem>
          <MenuDivider />
          <MenuItem
            as="a"
            href={`/lesson?scene=${sceneSlug}`}
            target="_blank"
            rel="noreferrer"
            title="Open the live lesson page in a new tab"
          >
            ↗ Open in lesson page
          </MenuItem>
          <MenuItem
            onClick={() => setAutoSave((v) => !v)}
            title="Auto-save draft 2s after the last edit"
          >
            {autoSave ? "✓ Auto-save (on)" : "Auto-save (off)"}
          </MenuItem>
          <MenuDivider />
          <MenuItem onClick={() => setShowHelp(true)}>
            Keyboard shortcuts… (?)
          </MenuItem>
        </OverflowMenu>
      </div>
      <main style={{
        display: "flex",
        flex: 1, minHeight: 0,
        alignItems: "stretch",
        gap: 0,
        padding: 0,
      }}>
        {/* Sidebar */}
        <aside className="editor-panel" style={{
          width: sidebarCollapsed ? 0 : sidebarWidth,
          minWidth: sidebarCollapsed ? 0 : sidebarWidth,
          maxWidth: sidebarCollapsed ? 0 : sidebarWidth,
          overflowY: "auto",
          overflowX: "hidden",
          borderRight: "1px solid var(--border)",
          borderRadius: 0,
          boxShadow: "none",
          padding: sidebarCollapsed ? 0 : undefined,
          transition: "width 0.18s ease, min-width 0.18s ease, max-width 0.18s ease",
        }}>
          {/* Mode toggle */}
          <div role="tablist" aria-label="Editor mode" style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            marginBottom: 12,
            borderBottom: "1px solid var(--border)",
          }}>
            <ModeTab
              active={editorMode === "sam"}
              onClick={() => setEditorMode("sam")}
              icon="✦"
              label="SAM"
              hint="Click to segment"
            />
            <ModeTab
              active={editorMode === "edit"}
              onClick={() => setEditorMode("edit")}
              icon="✎"
              label="Edit"
              hint="Drag points"
            />
          </div>
          {selectedItem && editorMode === "edit" && selectedItem.polygons.length >= 1 && (
            <div style={{
              marginBottom: 12,
              fontSize: "0.78rem", color: "var(--text-muted)",
              padding: "6px 8px", borderRadius: 0,
              background: "var(--surface-sunken, rgba(0,0,0,0.04))",
            }}>
              <div style={{ marginBottom: 6 }}>
                Polygon {activePolyIdx + 1} of {selectedItem.polygons.length}
                <span style={{ opacity: 0.6, marginLeft: 6, fontSize: "0.72rem" }}>
                  (Backspace to delete)
                </span>
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(26px, 1fr))",
                gap: 4,
              }}>
                {selectedItem.polygons.map((_, pi) => (
                  <button key={pi} type="button"
                    onClick={() => setActivePolyIdx(pi)}
                    style={{
                      height: 24, borderRadius: 0, border: "1px solid var(--border)",
                      cursor: "pointer", fontWeight: 600, fontSize: "0.72rem",
                      background: pi === activePolyIdx ? selectedColor : "var(--surface)",
                      color: pi === activePolyIdx ? "#fff" : "var(--text-muted)",
                      padding: 0,
                    }}>
                    {pi + 1}
                  </button>
                ))}
              </div>
            </div>
          )}
          <p className="section-label">Add items</p>
          <form onSubmit={(e) => { e.preventDefault(); addItem(); }}
            style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            <input ref={addInputRef} type="text" placeholder="e.g. pot, rug, lantern... (n)"
              value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
              style={{
                flex: 1, padding: "6px 10px", borderRadius: 0,
                border: "1px solid var(--border)", background: "var(--surface)",
                color: "var(--text)", fontSize: "0.9rem",
              }} />
            <button type="submit" className="secondary-button"
              disabled={!newLabel.trim()} style={{ padding: "6px 14px", borderRadius: 0 }}>+</button>
          </form>

          <p className="section-label">
            Items ({filter ? `${filteredItems.length} of ${items.length}` : items.length})
            {!filter && unmappedCount > 0 && <span style={{ fontWeight: 400, opacity: 0.7, marginLeft: 6 }}>— {unmappedCount} unmapped</span>}
            {!filter && overlappingIds.size > 0 && (
              <span style={{ fontWeight: 400, color: "var(--error, #e53e3e)", marginLeft: 6 }}>
                — {overlappingIds.size} overlapping
              </span>
            )}
          </p>
          {items.length > 6 && (
            <input ref={filterInputRef} type="search" placeholder="Filter items... (/)"
              value={filter} onChange={(e) => setFilter(e.target.value)}
              style={{
                width: "100%", padding: "6px 10px", marginBottom: 8,
                borderRadius: 0, border: "1px solid var(--border)",
                background: "var(--surface)", color: "var(--text)", fontSize: "0.85rem",
              }} />
          )}
          <ul className="editor-list">
            {filteredItems.map((item) => {
              const i = items.indexOf(item);
              const n = item.polygons.length;
              const sel = item.id === selectedId;
              const color = PALETTE[i % PALETTE.length];
              const pend = queue.filter((q) => q.itemId === item.id && (q.status === "queued" || q.status === "processing")).length;
              const overlap = overlappingIds.has(item.id);
              const isDragTarget = dropTargetId === item.id && draggingId !== null && draggingId !== item.id;
              const isDragging = draggingId === item.id;
              return (
                <li key={item.id}>
                  <div className={`editor-item${sel ? " is-active" : ""}`}
                    draggable
                    onDragStart={(e) => {
                      setDraggingId(item.id);
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", item.id);
                    }}
                    onDragEnd={() => { setDraggingId(null); setDropTargetId(null); }}
                    onDragOver={(e) => {
                      if (draggingId && draggingId !== item.id) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        if (dropTargetId !== item.id) setDropTargetId(item.id);
                      }
                    }}
                    onDragLeave={(e) => {
                      // only clear if leaving to outside the row, not to a child element
                      if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
                        if (dropTargetId === item.id) setDropTargetId(null);
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (draggingId && draggingId !== item.id) {
                        pushHistory();
                        reorderItem(draggingId, item.id);
                      }
                      setDraggingId(null);
                      setDropTargetId(null);
                    }}
                    onMouseEnter={() => setHoveredId(item.id)}
                    onMouseLeave={() => setHoveredId((cur) => (cur === item.id ? null : cur))}
                    onClick={() => { setSelectedId(sel ? null : item.id); setActivePolyIdx(0); }}
                    style={{
                      cursor: isDragging ? "grabbing" : "pointer",
                      opacity: isDragging ? 0.4 : 1,
                      borderRadius: 0,
                      borderLeft: isDragTarget
                        ? `3px solid #4363d8`
                        : sel
                          ? `3px solid var(--accent)`
                          : overlap
                            ? `3px solid var(--error, #e53e3e)`
                            : `3px solid transparent`,
                      paddingLeft: 6,
                    }}>
                    <span className="editor-item-row">
                      <span style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 18, height: 18, borderRadius: 9, flexShrink: 0, marginRight: 6,
                        border: `2px solid ${color}`, backgroundColor: n > 0 ? color : "transparent",
                        fontSize: "0.6rem", color: n > 0 ? "#fff" : color, fontWeight: 700,
                      }}>
                        {n > 0 ? n : "?"}
                      </span>
                      <span className="editor-item-label-wrap" style={{ flex: 1, minWidth: 0 }}>
                        {editingId === item.id ? (
                          <input type="text" value={editingLabel}
                            onChange={(e) => setEditingLabel(e.target.value)}
                            onBlur={commitRename}
                            onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setEditingId(null); }}
                            autoFocus onClick={(e) => e.stopPropagation()}
                            style={{ font: "inherit", padding: "2px 4px", border: "1px solid var(--accent)", borderRadius: 4, background: "var(--surface)", color: "var(--text)", width: "100%" }} />
                        ) : (
                          <span className="editor-item-label"
                            onDoubleClick={(e) => { e.stopPropagation(); startRename(item); }}
                            title="Double-click to rename"
                            style={{ opacity: n > 0 ? 1 : 0.7, fontStyle: n > 0 ? "normal" : "italic" }}>
                            {item.english}
                          </span>
                        )}
                        {pend > 0 && <span style={{ fontSize: "0.75rem", opacity: 0.6, marginLeft: 4 }}>({pend}...)</span>}
                        {overlappingIds.has(item.id) && (
                          <span title="Overlaps another item"
                            style={{
                              fontSize: "0.7rem", marginLeft: 6, padding: "1px 6px",
                              borderRadius: 4, color: "#fff", background: "var(--error, #e53e3e)",
                              fontWeight: 600,
                            }}>
                            overlap
                          </span>
                        )}
                      </span>
                      <span style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                        <button type="button" className="editor-item-review" title="Remove"
                          onClick={(e) => { e.stopPropagation(); removeItem(item.id); }}>×</button>
                      </span>
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>

          {error && <p style={{ padding: "8px 0", color: "var(--error, #e53e3e)", fontSize: "0.85rem" }}>{error}</p>}

          <input ref={importInputRef} type="file" accept="application/json,.json"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) importScene(file);
              e.target.value = "";
            }} />
        </aside>

        {/* Drag handle (4px wide, expands hit area on hover) */}
        {!sidebarCollapsed && (
          <div
            onPointerDown={onResizeStart}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeEnd}
            onPointerCancel={onResizeEnd}
            title="Drag to resize · double-click to collapse"
            onDoubleClick={() => setSidebarCollapsed(true)}
            style={{
              width: 6, flexShrink: 0,
              cursor: "col-resize",
              background: "var(--border)",
              touchAction: "none",
            }}
          />
        )}
        {sidebarCollapsed && (
          <button
            type="button"
            onClick={() => setSidebarCollapsed(false)}
            title="Show sidebar"
            style={{
              width: 18, flexShrink: 0,
              border: "none", borderRight: "1px solid var(--border)",
              background: "var(--surface)", color: "var(--text-muted)",
              cursor: "pointer", fontSize: "0.85rem",
            }}
          >›</button>
        )}

        {/* Image + toolbar */}
        <section style={{
          flex: 1, minWidth: 0,
          display: "flex", flexDirection: "column",
          padding: 0, overflow: "hidden",
        }}>
          <div style={{
            flex: 1, minHeight: 0, position: "relative",
            display: "flex", alignItems: "flex-start", justifyContent: "flex-start",
            overflow: "hidden",
          }}>
          <div className="editor-scene" style={{
            // Width-bound by default but capped at the available height so the
            // image never overflows its container (no scrollbar).
            aspectRatio: `${sceneWidth} / ${sceneHeight}`,
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
            borderRadius: 0,
            border: "none",
            cursor: editorMode === "sam"
              ? (canClick ? "crosshair" : "default")
              : "default",
          }}>
            <img src={scene.image} alt={scene.alt} width={sceneWidth} height={sceneHeight}
              style={{ display: "block", width: "100%", height: "100%", objectFit: "contain" }} />
            <svg ref={overlayRef} className="editor-overlay"
              viewBox={`0 0 ${sceneWidth} ${sceneHeight}`}
              onClick={editorMode === "sam" ? handleSamClick : handleEditClick}
              onPointerDown={editorMode === "edit" ? handleEditPointerDown : undefined}
              onPointerMove={editorMode === "edit" ? handlePointerMove : undefined}
              onPointerUp={editorMode === "edit" ? handlePointerUp : undefined}
              onPointerLeave={editorMode === "edit" ? handlePointerUp : undefined}
            >
              {/* Hover-preview: render the hovered (but not selected) item's polygons faintly */}
              {hoveredId && hoveredId !== selectedId && (() => {
                const hovered = items.find((it) => it.id === hoveredId);
                if (!hovered) return null;
                const idx = items.indexOf(hovered);
                const color = idx >= 0 ? PALETTE[idx % PALETTE.length] : "#888";
                return hovered.polygons.map((poly, pi) => (
                  <polygon key={`hover-${pi}`}
                    points={pointsToString(poly)}
                    fill={color}
                    fillOpacity={0.12}
                    stroke={color}
                    strokeWidth={1}
                    strokeDasharray="4 3"
                    style={{ pointerEvents: "none" }}
                  />
                ));
              })()}
              {/* Only selected item's polygons */}
              {selectedItem && selectedItem.polygons.map((poly, pi) => (
                <polygon key={`sel-${pi}`}
                  points={pointsToString(poly)}
                  fill={selectedColor}
                  fillOpacity={editorMode === "edit" && pi === activePolyIdx ? 0.4 : 0.25}
                  stroke={selectedColor}
                  strokeWidth={editorMode === "edit" && pi === activePolyIdx ? 3 : 1.5}
                  style={{ pointerEvents: "none" }}
                />
              ))}
              {/* Edit mode: draggable handles for active polygon */}
              {editorMode === "edit" && selectedItem && selectedItem.polygons[activePolyIdx]?.map((pt, pi) => (
                <circle key={`h-${pi}`}
                  cx={pt[0]} cy={pt[1]} r={6}
                  fill={selectedColor} stroke="#fff" strokeWidth={1.5}
                  style={{ cursor: "grab" }}
                  onPointerDown={(e) => handleHandlePointerDown(e, activePolyIdx, pi)}
                  onClick={(e) => handleHandleClick(e as unknown as ReactPointerEvent<SVGCircleElement>, activePolyIdx, pi)}
                />
              ))}
              {/* SAM mode: queued click markers */}
              {editorMode === "sam" && queue
                .filter((q) => q.status === "queued" || q.status === "processing")
                .map((q) => (
                  <g key={q.id}>
                    <circle cx={q.x} cy={q.y}
                      r={q.status === "processing" ? 12 : 8}
                      fill={selectedColor} fillOpacity={0.5}
                      stroke="#fff" strokeWidth={2}
                      style={{ pointerEvents: "none" }} />
                    {q.status === "processing" && (
                      <circle cx={q.x} cy={q.y} r={16}
                        fill="none" stroke={selectedColor} strokeWidth={2} strokeDasharray="6 4"
                        style={{ pointerEvents: "none" }}>
                        <animateTransform attributeName="transform" type="rotate"
                          from={`0 ${q.x} ${q.y}`} to={`360 ${q.x} ${q.y}`}
                          dur="1s" repeatCount="indefinite" />
                      </circle>
                    )}
                  </g>
                ))}
            </svg>
            {samStatus === "ready" && !selectedId && items.length > 0 && (
              <div style={{
                position: "absolute", inset: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "rgba(0,0,0,0.2)", color: "#fff",
                fontSize: "1rem", fontWeight: 600, pointerEvents: "none", borderRadius: "inherit",
              }}>
                Select an item to start
              </div>
            )}
            {(samStatus === "downloading" || samStatus === "encoding" || samStatus === "failed") && (
              <div style={{
                position: "absolute", inset: 0, zIndex: 20,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: 12,
                background: "rgba(15,15,18,0.6)", color: "#fff",
                pointerEvents: "auto", cursor: "wait",
              }}>
                {samStatus !== "failed" ? (
                  <>
                    <div style={{
                      width: 36, height: 36, borderRadius: 999,
                      border: "3px solid rgba(255,255,255,0.25)",
                      borderTopColor: "#fff",
                      animation: "spin 0.9s linear infinite",
                    }} />
                    <p style={{ margin: 0, fontWeight: 600, fontSize: "0.95rem" }}>
                      {samStatus === "downloading" ? "Downloading SAM model…" : "Encoding image…"}
                    </p>
                    {samProgress && (
                      <p style={{ margin: 0, fontSize: "0.8rem", opacity: 0.8 }}>{samProgress}</p>
                    )}
                  </>
                ) : (
                  <p style={{ margin: 0, fontWeight: 600, fontSize: "0.95rem", color: "#fca5a5" }}>
                    SAM failed to load — check the browser console
                  </p>
                )}
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
          </div>
          </div>
        </section>
      </main>
      {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} />}
      {diffPayload && (
        <DiffOverlay
          diff={diffPayload}
          onCancel={() => setDiffPayload(null)}
          onConfirm={confirmPublish}
        />
      )}
      {saveMsg && (
        <div
          role="status"
          style={{
            position: "fixed", bottom: 16, right: 16, zIndex: 50,
            padding: "10px 16px", borderRadius: 8,
            background: saveMsg.startsWith("Error") ? "var(--error, #e53e3e)" : "var(--accent)",
            color: saveMsg.startsWith("Error") ? "#fff" : onAccent,
            fontSize: "0.9rem", fontWeight: 600,
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
            maxWidth: 360,
          }}
        >
          {saveMsg}
        </div>
      )}
    </div>
  );
}

function ModeTab({
  active, onClick, icon, label, hint,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        position: "relative",
        padding: "10px 8px 11px",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        color: active ? "var(--text)" : "var(--text-muted)",
        fontWeight: active ? 700 : 500,
        fontSize: "0.9rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        lineHeight: 1.1,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span aria-hidden="true" style={{ fontSize: "0.95rem", opacity: active ? 1 : 0.7 }}>{icon}</span>
        {label}
      </span>
      <span style={{ fontSize: "0.7rem", fontWeight: 400, opacity: 0.7 }}>{hint}</span>
      {/* Active underline indicator */}
      <span aria-hidden="true" style={{
        position: "absolute", left: 0, right: 0, bottom: -1,
        height: 2,
        background: active ? "var(--brand, var(--accent))" : "transparent",
      }} />
    </button>
  );
}

function OverflowMenu({ children, disabled }: { children: React.ReactNode; disabled?: boolean }) {
  const [openInternal, setOpenInternal] = useState(false);
  // While disabled, keep the menu shut. Derived state via render guard,
  // not via setState-in-effect (which the lint rule rejects).
  const open = disabled ? false : openInternal;
  const setOpen = useCallback((next: boolean | ((v: boolean) => boolean)) => {
    if (disabled) return;
    setOpenInternal(next);
  }, [disabled]);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, setOpen]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="secondary-button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{ padding: "6px 10px", fontSize: "1rem", fontWeight: 700, lineHeight: 1 }}
      >
        ⋯
      </button>
      {open && (
        <div
          role="menu"
          onClick={() => setOpen(false)}
          style={{
            position: "absolute", top: "calc(100% + 4px)", right: 0,
            minWidth: 200, zIndex: 30,
            background: "var(--bg, #fff)", color: "var(--text)",
            border: "1px solid var(--border)", borderRadius: 6,
            boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
            padding: 4,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

type MenuItemProps =
  | { as?: "button"; onClick: () => void; disabled?: boolean; title?: string; children: React.ReactNode }
  | { as: "a"; href: string; target?: string; rel?: string; title?: string; children: React.ReactNode };

function MenuItem(props: MenuItemProps) {
  const baseStyle: React.CSSProperties = {
    display: "block", width: "100%", textAlign: "left",
    padding: "7px 10px", borderRadius: 4,
    background: "transparent", border: "none", cursor: "pointer",
    fontSize: "0.86rem", color: "inherit", textDecoration: "none",
    fontFamily: "inherit", whiteSpace: "nowrap",
  };
  if (props.as === "a") {
    return (
      <a href={props.href} target={props.target} rel={props.rel} title={props.title}
        role="menuitem" style={baseStyle}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-sunken, rgba(0,0,0,0.06))")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        {props.children}
      </a>
    );
  }
  return (
    <button type="button" onClick={props.onClick} disabled={props.disabled} title={props.title}
      role="menuitem"
      style={{ ...baseStyle, opacity: props.disabled ? 0.45 : 1, cursor: props.disabled ? "not-allowed" : "pointer" }}
      onMouseEnter={(e) => { if (!props.disabled) e.currentTarget.style.background = "var(--surface-sunken, rgba(0,0,0,0.06))"; }}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {props.children}
    </button>
  );
}

function MenuDivider() {
  return <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />;
}

function HelpOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg, #fff)", color: "var(--text)",
          borderRadius: 12, padding: "20px 24px",
          maxWidth: 480, width: "100%",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Keyboard shortcuts</h2>
          <button type="button" onClick={onClose}
            style={{
              border: "none", background: "transparent", cursor: "pointer",
              fontSize: "1.4rem", color: "var(--text-muted)", padding: 0, lineHeight: 1,
            }}>×</button>
        </div>
        <table style={{ width: "100%", fontSize: "0.88rem", borderCollapse: "collapse" }}>
          <tbody>
            {[
              ["1", "Switch to SAM mode"],
              ["2", "Switch to Edit mode"],
              ["Ctrl/⌘ + S", "Save draft"],
              ["Ctrl/⌘ + Z", "Undo"],
              ["Ctrl/⌘ + Shift + Z", "Redo"],
              ["Ctrl/⌘ + Y", "Redo (alt)"],
              ["Backspace / Delete", "Delete the active polygon (Edit mode)"],
              ["Tab / Shift+Tab", "Cycle through item's polygons (Edit mode)"],
              ["/", "Focus the items filter"],
              ["n", "Focus the add-item input"],
              ["?", "Toggle this overlay"],
              ["Esc", "Close any modal"],
            ].map(([k, v]) => (
              <tr key={k}>
                <td style={{ padding: "6px 12px 6px 0", whiteSpace: "nowrap" }}>
                  <kbd style={{
                    fontFamily: "inherit", fontSize: "0.8rem", fontWeight: 600,
                    padding: "2px 6px", borderRadius: 4,
                    border: "1px solid var(--border)", background: "var(--surface)",
                  }}>{k}</kbd>
                </td>
                <td style={{ padding: "6px 0" }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <hr style={{ margin: "14px 0", border: 0, borderTop: "1px solid var(--border)" }} />
        <h3 style={{ margin: "0 0 8px 0", fontSize: "0.92rem" }}>Mouse</h3>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: "0.85rem", lineHeight: 1.7 }}>
          <li><strong>SAM mode:</strong> click an object to segment it</li>
          <li><strong>Edit mode:</strong> drag a point to move it; drag inside a polygon to move the whole shape; <kbd style={{ fontSize: "0.78rem" }}>Shift</kbd>+click an edge to insert a point; <kbd style={{ fontSize: "0.78rem" }}>Alt</kbd>+click a point to delete it</li>
          <li>Double-click an item label to rename it</li>
        </ul>
      </div>
    </div>
  );
}
